import { FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { Box } from '@mui/system';
import React from 'react';
import { useOutletContext } from 'react-router-dom';

import { TabPaper } from '@/components/atoms/TabPaper';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { ColorSchemeContext } from '@/context/colorSchemeContext';
import { UserPreferencesContextType } from '@/DTOs/userPreferences';
import { useTranslator } from '@/hooks/useTranslator';

export const SettingsAppearanceTab: React.FC = (): React.ReactElement => {
  const context = useOutletContext<UserPreferencesContextType>();
  const { preferences, loading, update } = context;

  const { theme = 'light' } = preferences.appearance ?? {};

  const setPaletteMode = React.useContext<(mode: 'light' | 'dark') => void>(ColorSchemeContext);
  const tr = useTranslator();

  const [selectedTheme, setSelectedTheme] = React.useState<'light' | 'dark'>(theme);

  const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    if (value === 'light' || value === 'dark') {
      setSelectedTheme(value);
      return;
    }
  }, []);

  const handleSave = React.useCallback(async (): Promise<void> => {
    await update({ appearance: { theme: selectedTheme } });
    setPaletteMode(selectedTheme);
  }, [selectedTheme, setPaletteMode, update]);

  return (
    <TabPaper>
      <Box>
        <Typography variant="h7" component="h6">
          {tr('THEME')}
        </Typography>
        <Box width={422} mt={2}>
          <RadioGroup value={selectedTheme} onChange={handleChange}>
            <FormControlLabel
              value="light"
              control={<Radio />}
              label={
                <Typography variant="body2" color="textPrimary" lineHeight="42px">
                  {tr('LIGHT')}
                </Typography>
              }
            />
            <FormControlLabel
              value="dark"
              control={<Radio />}
              label={
                <Typography variant="body2" color="textPrimary" lineHeight="42px">
                  {tr('DARK')}
                </Typography>
              }
            />
          </RadioGroup>
        </Box>
        <Box mt={2}>
          <LoadingButton variant="dark" loading={loading} onClick={handleSave}>
            {tr('SAVE_CHANGES')}
          </LoadingButton>
        </Box>
      </Box>
    </TabPaper>
  );
};
